// *******************************************************************************
// Form handling
// *******************************************************************************
const getFilesByKey = (element) => {
  const container = element.closest('.upload-box');
  const { uploadKey } = container.dataset;

  return filesToUpload.filter((item) => item?.parentId === uploadKey);
};

const validatorWrapper = (input, callback) => {
  try {
    const files = getFilesByKey(input?.element);
    return callback(files, input) || { valid: true };
  } catch (e) {
    console.error('Error validating file:', e);
    return { valid: false };
  }
};

const fileCountValidator = () => ({
  validate: (input) =>
    validatorWrapper(input, (files, input) => {
      const required = input.element.hasAttribute('required');
      const { fvNotEmpty___message, maxFiles, fvFile___maxFiles___message } = input.element.dataset;
      if (required && files.length === 0) return { valid: false, message: fvNotEmpty___message };
      if (files.length > parseInt(maxFiles)) return { valid: false, message: fvFile___maxFiles___message };
      return { valid: true };
    }),
});

const fileSizeValidator = () => ({
  validate: (input) =>
    validatorWrapper(input, (files, input) => {
      const required = input.element.hasAttribute('required');

      if (!required && files.length === 0) {
        return { valid: true };
      }

      const {
        fvFile___maxSize: maxSize,
        fvFile___maxTotalSize: maxTotal,
        fvFile___minSize: minSize,
        fvFile___minTotalSize: minTotal,
        fvFile___maxSize___message: maxSizeMessage,
        fvFile___minSize___message: minSizeMessage,
        fvFile___maxTotalSize___message: maxTotalMessage,
        fvFile___minTotalSize___message: minTotalMessage,
      } = input.element.dataset;

      let totalSize = 0;

      for (const { file } of files) {
        if (maxSize && file.size > parseInt(maxSize)) {
          return { valid: false, message: maxSizeMessage };
        }

        if (minSize && file.size < parseInt(minSize)) {
          return { valid: false, message: minSizeMessage };
        }

        totalSize += file.size;
      }

      if (maxTotal && totalSize > parseInt(maxTotal)) {
        return { valid: false, message: maxTotalMessage };
      }

      if (minTotal && totalSize < parseInt(minTotal)) {
        return { valid: false, message: minTotalMessage };
      }

      return { valid: true };
    }),
});

const fileTypeValidator = () => ({
  validate: (input) =>
    validatorWrapper(input, (files, input) => {
      const required = input.element.hasAttribute('required');

      if (!required && files.length === 0) {
        return { valid: true };
      }

      const { fvFile___extension: extension, fvFile___extension___message: extensionMessage } = input.element.dataset;

      if (!extension) {
        return { valid: true };
      }

      const types = extension
        .split(',')
        .map((type) => type.trim().toLowerCase())
        .filter(Boolean);

      for (const { file } of files) {
        if (!file?.name) {
          return { valid: false, message: extensionMessage };
        }

        const nameParts = file.name.split('.');
        const fileType = nameParts.length > 1 ? nameParts.pop().toLowerCase() : '';

        if (!fileType || !types.includes(fileType)) {
          return { valid: false, message: extensionMessage };
        }
      }
      return { valid: true };
    }),
});

const handleForm = (form, formAction) => {
  const formParent = form.closest('[data-parent]');
  const submitBtn = form.querySelector('.form-component__submit .btn');
  const passwordField = form.querySelector('#setPassword');
  const uploadBox = form.querySelector('input[type="file"]');

  if (!formParent) {
    return;
  }
  const parentSection = form.closest('.sc-general');
  const formAlertError = formParent.querySelector('.form-component__alert-error');

  const emailError = getEmailError(form);
  const emailFields = formParent.querySelectorAll('input[type="email"]'); // Get all email fields

  const fieldValidators = {};
  emailFields.forEach((field) => {
    const fieldName = field.name;

    if (fieldName) {
      fieldValidators[fieldName] = {
        validators: {
          callback: {
            message: emailError,
            callback(input) {
              return validateEmail(input.value);
            },
          },
        },
      };
    }
  });

  const fv = FormValidation.formValidation(form, {
    locale: 'sl_SI',
    localization: FormValidation.locales.sl_SI,
    fields: fieldValidators,
    plugins: {
      message: new FormValidation.plugins.Message({
        container: (field, element) =>
          element.closest('.mod-input,.iti, .checkbox,.dropdown,.radio-button,.upload-box,.form-component__item'),
      }),
      declarative: new FormValidation.plugins.Declarative({
        html5Input: true,
      }),
      trigger: new FormValidation.plugins.Trigger({
        event: 'submit',
      }),
      excluded: new FormValidation.plugins.Excluded(),
      submitButton: new FormValidation.plugins.SubmitButton(),
    },
  })

    .registerValidator('checkMaxFiles', fileCountValidator)
    .registerValidator('checkFileSize', fileSizeValidator)
    .registerValidator('checkTotalFileSize', fileSizeValidator)
    .registerValidator('checkFileType', fileTypeValidator)

    .on('core.element.validated', (e) => {
      if (e.element.hasAttribute('required')) {
        const value = fv.getElementValue(e.field, e.element);
        const container = FormValidation.utils.closest(
          e.element,
          '.mod-input, .iti, .checkbox, .dropdown, .radio-button,.upload-box,.form-component__item',
        );
        const isUploadBoxInput = e.element.type === 'file' && e.element.closest('.upload-box');
        const isValidationFailed = isUploadBoxInput ? !e.valid : value === '' || !e.valid;

        FormValidation.utils.classSet(container, {
          'is-validation-failed': isValidationFailed,
        });
      }
    })
    .on('core.form.valid', () => {
      const formType = form.querySelector('[data-form-type]').value;
      const formValue = getFormValue(form, fv);

      const hasInvalidPhone = formParent.querySelector('.mod-telephone .is-validation-failed');
      if (hasInvalidPhone) return;

      grecaptcha.ready(async () => {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
        formParent.classList.add('show-loader');
        try {
          const files = {};
          Array.from(filesToUpload).forEach((file, index) => {
            files[`files_${index + 1}`] = file.file;
          });

          const response = await onFormSubmit({
            formData: {
              ...formValue,
              ...files,
              id: localData.id,
              lang: localData.lang,
              formType,
            },
            recaptchaKey: form.querySelector('[data-sitekey]').getAttribute('data-sitekey'),
            recaptchaAction: formAction,
          });

          if (response?.data?.data?.url) {
            window.location.href = response.data.data.url;
            return;
          }

          formParent.classList.remove('show-loader');
          submitBtn.classList.remove('is-loading');
          formParent.classList.add('show-thankyou');
          const hasToaster = formParent.classList.contains('has-toaster');

          if (parentSection) {
            parentSection.classList.add('show-thankyou');

            const scrollPosition =
              parentSection.getBoundingClientRect().top +
              window.scrollY -
              (document.querySelector('.header-main')?.clientHeight || 0);
            window.scrollTo({
              top: scrollPosition,
              behavior: 'smooth',
            });
          }

          // Toaster success
          if (!hasToaster) {
            return;
          }
          setTimeout(() => {
            formParent.classList.remove('show-thankyou');
          }, 2000);
        } catch (error) {
          submitBtn.classList.remove('is-loading');
          formParent.classList.remove('show-loader');
          formParent.classList.add('show-error');
          formAlertError.classList.remove('hide');
          formAlertError.innerHTML = error.response.data.data.msg;
        }
        submitBtn.disabled = false;
      });
    });

  // File upload validation
  if (uploadBox) {
    const uploadBoxContainer = uploadBox.closest('.upload-box');

    fv.addField(uploadBox.name, {
      validators: {
        // Required is enforced via checkMaxFiles + filesToUpload; native input is always cleared.
        notEmpty: { enabled: false },
        // Declarative data-fv-file___* attrs register the built-in file validator on the cleared input.
        file: { enabled: false },
        checkMaxFiles: {},
        checkFileSize: {},
        checkTotalFileSize: {},
        checkFileType: {},
      },
    });

    const validateUploadField = () => {
      setTimeout(() => {
        fv.validateField(uploadBox.name);
      }, 0);
    };

    // Clear stale submit-time error before upload-box runs its change handler (capture phase).
    uploadBox.addEventListener(
      'change',
      () => {
        uploadBoxContainer?.classList.remove('is-validation-failed');
      },
      true,
    );

    uploadBox.addEventListener('change', validateUploadField);

    attachEvent('.upload-box__item-remove', 'click', () => {
      validateUploadField();
    });
  }

  // Password validation
  if (passwordField) {
    const getValidationElem = (parent, selector) => parent?.querySelector(selector);

    const passwordFieldParent = passwordField.closest('.mod-input');

    const [notEmptyMsg, capitalLettersMsg, lowerCaseMsg, digitMsg, minCharactersMsg, specialCharacter] = [
      '[data-fv-not-empty___message]',
      '[data-password-capital-letters]',
      '[data-password-lowercase]',
      '[data-password-digit]',
      '[data-password-min-characters]',
      '[data-password-special-character]',
    ].map((query) => getValidationElem(passwordFieldParent, query));

    const allValidationItems = passwordFieldParent.querySelectorAll('.mod-input__password-validation-item');
    const confirmPassValidationElem = document.getElementById('confirmResetPassword');

    const toggleValidationClass = (elem, failed) => {
      if (!elem) return;

      elem.classList.toggle('is-failed', failed);
      elem.classList.toggle('is-passed', !failed);
    };

    const strongPassword = () => ({
      validate(input) {
        const password = input.value;

        if (!password) {
          allValidationItems.forEach((elem) => {
            elem.classList.remove('is-failed', 'is-passed');
          });

          passwordFieldParent.classList.remove('is-validation-failed');

          return { valid: false };
        }

        const checks = [
          {
            failed: !/[A-Z]/.test(password),
            element: capitalLettersMsg,
          },
          {
            failed: !/[a-z]/.test(password),
            element: lowerCaseMsg,
          },
          {
            failed: !/\d/.test(password),
            element: digitMsg,
          },
          {
            failed: !/[!@#$%^&*()_\-+=<>?]/.test(password),
            element: specialCharacter,
          },
          {
            failed: password.length < 8,
            element: minCharactersMsg,
          },
        ];

        checks.forEach(({ failed, element }) => {
          toggleValidationClass(element, failed);
        });

        const hasFailedRule = checks.some(({ failed }) => failed);

        passwordFieldParent.classList.toggle('is-validation-failed', hasFailedRule);

        return { valid: !hasFailedRule };
      },
    });

    const passwordMessage = notEmptyMsg?.getAttribute('data-fv-not-empty___message') || '';

    fv.addField('setPassword', {
      validators: {
        notEmpty: {
          message: passwordMessage,
        },
        checkPassword: {
          message: passwordMessage,
        },
      },
    });

    if (confirmPassValidationElem) {
      fv.addField('confirmResetPassword', {
        validators: {
          identical: {
            compare() {
              return form.querySelector('[name="setPassword"]').value;
            },
            message: confirmPassValidationElem.getAttribute('data-fv-identical___message') || '',
          },
        },
      });
    }

    fv.registerValidator('checkPassword', strongPassword);

    const updateSubmitButton = async () => {
      const passwordStatus = await fv.validateField('setPassword');

      let confirmStatus = 'Valid';

      if (confirmPassValidationElem) {
        confirmStatus = await fv.validateField('confirmResetPassword');
      }

      submitBtn.disabled = passwordStatus !== 'Valid' || confirmStatus !== 'Valid';
    };

    submitBtn.disabled = true;

    passwordField.addEventListener('input', async () => {
      await fv.validateField('setPassword');

      if (confirmPassValidationElem) {
        await fv.validateField('confirmResetPassword');
      }

      updateSubmitButton();
    });

    if (confirmPassValidationElem) {
      confirmPassValidationElem.addEventListener('input', updateSubmitButton);
    }
  }
};

document.addEventListener('DOMContentLoaded', () => {
  ['form_component', 'collection_form'].forEach((id) => {
    const form = document.querySelector(`#${id}`);
    if (form !== null) {
      const formAction = form.querySelector('[data-action]').value;
      handleForm(form, formAction);
    }
  });
});

// Check if all inputGroups in group have same name
const inputGroupParents = document.querySelectorAll('[data-input-group-parent]');

inputGroupParents.forEach((formRow) => {
  const inputGroups = formRow.querySelectorAll('[data-input-group-field]');

  if (inputGroups.length > 1) {
    // Check if failed
    const observer = new MutationObserver(() => {
      const inputParents = formRow.querySelectorAll('[data-input-group-item]');
      const hasFailedRadio = [...inputParents].some((inputParent) =>
        inputParent.classList.contains('is-validation-failed'),
      );
      formRow.classList.toggle('failed', hasFailedRadio);
    });

    inputGroups.forEach((input) =>
      observer.observe(input.closest('[data-input-group-item]'), { attributes: true, attributeFilter: ['class'] }),
    );

    // Get the first error message and create a new div
    if (formRow.classList.contains('input-group')) {
      const errorMessage = formRow.querySelector('[data-fv-not-empty___message]');
      if (errorMessage) {
        const groupErrorMessage = document.createElement('div');
        groupErrorMessage.classList.add('fv-plugins-message-container', 'group-error');
        groupErrorMessage.innerHTML = errorMessage.getAttribute('data-fv-not-empty___message');

        formRow.appendChild(groupErrorMessage);
      }
    }
  }
});

document.querySelectorAll('[data-toggle-field]').forEach((radio) => {
  const groupName = radio.name;
  const target = document.querySelector(`[data-field="${radio.dataset.toggleField}"]`);

  if (!target) return;

  const update = () => {
    const checkedRadio = document.querySelector(`[name="${groupName}"]:checked`);
    target.classList.toggle('hide', checkedRadio !== radio);
  };

  document.querySelectorAll(`[name="${groupName}"]`).forEach((r) => r.addEventListener('change', update));
  update();
});

// Toggle additional fields
attachEvent('.btn-additional-fields', 'click', (ev) => {
  const parentForm = ev.target.closest('.form-component');
  parentForm.classList.add('show-additional-field');
});
